import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Analytics.css';

// Charts - using recharts library
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

const Analytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [myPhotos, setMyPhotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [analyticsData, photosData] = await Promise.all([
        api.get('/analytics'),
        user?.role === 'user' ? api.get('/my-photos') : Promise.resolve(null)
      ]);
      setAnalytics(analyticsData);
      if (photosData) setMyPhotos(photosData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen error={error} onRetry={fetchData} />;

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>Tableau des analyses</h1>
        <button onClick={fetchData} className="btn-refresh">
          Actualiser
        </button>
      </div>

      {user?.role === 'photographer' ? (
        <PhotographerAnalytics analytics={analytics} />
      ) : (
        <UserAnalytics analytics={analytics} myPhotos={myPhotos} />
      )}
    </div>
  );
};

// Photographer Analytics View
const PhotographerAnalytics = ({ analytics }) => {
  const { summary, category_stats, recent_events } = analytics;

  const categoryData = Object.entries(category_stats || {}).map(([name, data]) => ({
    name, events: data.events, photos: data.photos, matches: data.matches
  }));

  const COLORS = ['#FF8C42', '#4CAF50', '#2196F3', '#9C27B0', '#FF5722'];

  return (
    <>
      <div className="stats-grid">
        <StatCard icon="📅" title="Événements" value={summary.total_events} color="#FF8C42" />
        <StatCard icon="📸" title="Photos" value={summary.total_photos.toLocaleString()} color="#4CAF50" />
        <StatCard icon="👥" title="Correspondances" value={summary.total_matches} color="#2196F3" />
        <StatCard icon="💰" title="Revenus" value={`${summary.total_revenue.toFixed(2)} F`} color="#FF5722" />
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Événements par catégorie</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={categoryData} dataKey="events" nameKey="name" label>
                {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Photos et correspondances</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="photos" fill="#4CAF50" />
              <Bar dataKey="matches" fill="#2196F3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="events-table-card">
        <h3>Événements récents</h3>
        <table className="events-table">
          <thead>
            <tr>
              <th>Événement</th><th>Date</th><th>Photos</th><th>Correspondances</th><th>Performance</th>
            </tr>
          </thead>
          <tbody>
            {recent_events?.map(e => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>{new Date(e.date).toLocaleDateString()}</td>
                <td>{e.photos_count}</td>
                <td>{e.matches_count}</td>
                <td>
                  <ProgressBar value={e.photos_count > 0 ? (e.matches_count / e.photos_count) * 100 : 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// User Analytics View
const UserAnalytics = ({ analytics, myPhotos }) => {
  const { summary, category_matches, statistics } = analytics;

  const categoryData = Object.entries(category_matches || {}).map(([name, value]) => ({ name, value }));
  const COLORS = ['#FF8C42', '#4CAF50', '#2196F3', '#9C27B0'];

  return (
    <>
      <div className="stats-grid">
        <StatCard icon="📸" title="Photos trouvées" value={summary.total_matches} color="#FF8C42" />
        <StatCard icon="📅" title="Événements" value={summary.total_events} color="#4CAF50" />
        <StatCard icon="🎯" title="Confiance moyenne" value={`${statistics.average_confidence}%`} color="#2196F3" />
        <StatCard icon="💰" title="Dépensé" value={`${summary.total_spent.toFixed(2)} F`} color="#FF5722" />
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Photos par catégorie</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={categoryData} dataKey="value" nameKey="name" label>
                {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Scores de confiance</h3>
          <div className="confidence-bars">
            <ConfidenceBar label="Moyenne" value={statistics.average_confidence} />
            <ConfidenceBar label="Plus élevée" value={statistics.highest_confidence} color="#4CAF50" />
            <ConfidenceBar label="Plus faible" value={statistics.lowest_confidence} color="#FF8C42" />
          </div>
        </div>
      </div>

      {myPhotos?.data && (
        <div className="my-photos-section">
          <h3>Mes photos par événement ({myPhotos.total_photos} au total)</h3>
          {myPhotos.data.map(event => (
            <EventPhotoGroup key={event.event_id} event={event} />
          ))}
        </div>
      )}
    </>
  );
};

// Components
const StatCard = ({ icon, title, value, color }) => (
  <div className="stat-card" style={{ borderColor: color }}>
    <span className="stat-icon" style={{ color }}>{icon}</span>
    <div className="stat-content">
      <div className="stat-title">{title}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  </div>
);

const ProgressBar = ({ value }) => (
  <div className="progress-bar">
    <div className="progress-fill" style={{ width: `${Math.min(value, 100)}%` }}>
      {value.toFixed(1)}%
    </div>
  </div>
);

const ConfidenceBar = ({ label, value, color = '#2196F3' }) => (
  <div className="confidence-item">
    <span>{label}</span>
    <div className="confidence-bar">
      <div className="confidence-fill" style={{ width: `${value}%`, backgroundColor: color }}>
        {value}%
      </div>
    </div>
  </div>
);

const EventPhotoGroup = ({ event }) => (
  <div className="event-photo-group">
    <div className="event-header">
      <h4>{event.event_name}</h4>
      <span className="event-category">{event.event_category}</span>
      <span className="photo-count">{event.photos.length} photos</span>
    </div>
    <div className="photos-grid">
      {event.photos.map(photo => (
        <div key={photo.id} className="photo-card">
          <img src={photo.url} alt={photo.filename} />
          <div className="photo-overlay">
            <span className="confidence-badge">{photo.confidence_score.toFixed(1)}%</span>
          </div>
          <div className="photo-name" title={getPhotoDisplayName(photo)}>
            {getPhotoDisplayName(photo)}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const LoadingScreen = () => (
  <div className="loading-screen">
    <div className="spinner"></div>
    <p>Chargement des analyses...</p>
  </div>
);

const ErrorScreen = ({ error, onRetry }) => (
  <div className="error-screen">
    <span className="error-icon">⚠️</span>
    <p>Erreur : {error}</p>
    <button onClick={onRetry} className="btn-retry">Réessayer</button>
  </div>
);

export default Analytics;
